// What a language has to provide for the dictation parser. The engine in
// parse.ts is language-neutral; everything word-specific lives in a lexicon.
// All word lists are "folded" (see fold.ts): lower case, no accents, Latin script.

import type { Language } from '@/i18n';
import type { Code, Material, Surface } from '@/utils/dental';

export type Token = { raw: string; norm: string };

/** A full FDI number read from one or more tokens. */
export type NumberRead = {
  value: number;
  /** How many tokens it took. */
  used: number;
  /** "hatos", "šesnaestica": said as "the 16", so clearly meant as a tooth. */
  adjectival: boolean;
  /** Range start / end marked on the word itself ("16-tól", "23-ig"). */
  from: boolean;
  to: boolean;
};

export type LocationWord = 'upper' | 'lower' | 'right' | 'left' | 'primary' | 'filler';

export type CodeRule = {
  code: Code;
  /** Token starts with one of these. */
  stems?: string[];
  /** Token equals one of these. */
  exact?: string[];
  /** A plain "híd" / "most": split into abutments and pontics over a range. */
  genericBridge?: boolean;
};

export type MaterialRule = { material: Material; stems: string[] };

export type PhraseHit =
  | { kind: 'code'; code: Code; used: number }
  | { kind: 'material'; material: Material; used: number };

export type Command = 'approve_all' | 'undo' | 'yes' | 'no';

export type Lexicon = {
  lang: Language;
  readNumber(tokens: Token[], i: number): NumberRead | null;
  /** "egy-hatos" / "jedan-šest": quadrant digit + position. */
  readQuadrantPair(a: string, b: string): number | null;
  /** "hatos", "šestica": a position (1..8) without a quadrant. */
  readPosition(norm: string): number | null;
  /**
   * Optional: a position that may span words ("first molar", "første molar")
   * or that only counts after a side word ("upper right six", not a bare "six").
   * Without it, `readPosition` on one token is used.
   */
  readPositionAt?(
    tokens: Token[],
    i: number,
    afterLocation: boolean
  ): { position: number; used: number } | null;
  location(norm: string): LocationWord | null;
  /** Range prepositions ("od 13 do 23"); Hungarian marks ranges with suffixes instead. */
  rangeFrom: ReadonlySet<string>;
  rangeTo: ReadonlySet<string>;
  /** Multi-word phrases ("nincs meg", "telo mosta"). */
  readPhrase(tokens: Token[], i: number): PhraseHit | null;
  codeRules: CodeRule[];
  surfaceStems: [string, Surface][];
  lowercaseSurfaceAbbr: ReadonlySet<string>;
  /** Raw tokens that look like surface letters but are words in this language. */
  excludedSurfaceAbbr: ReadonlySet<string>;
  materialRules: MaterialRule[];
  isPlanned(norm: string): boolean;
  /** Qualifier kept as a note ("szekunder", "duboki"), or null. */
  note(norm: string): string | null;
  negationWords: ReadonlySet<string>;
  correctionWords: ReadonlySet<string>;
  fillerWords: ReadonlySet<string>;
  separatorWords: ReadonlySet<string>;
  commandPatterns: { command: Command; pattern: RegExp }[];
  noChangePattern: RegExp;
  /**
   * A spoken price ("3000 dinara", "3 hiljade dinara"): the currency words and
   * the word for a thousand, as regex sources matched on the raw text
   * (case-insensitive, accents as spoken).
   */
  price: { unit: string; thousand: string };
  /**
   * Optional: split one written compound into words before reading
   * ("amalgamfylling" → "amalgam", "fylling"). Null keeps the token.
   */
  splitCompound?(norm: string): string[] | null;
  /** Mobility grade: "második fokú", "drugi stepen". */
  readGrade(tokens: Token[], i: number): { grade: string; used: number } | null;
};

/** Materials placed directly in the mouth: a bare "kompozit" means a filling. */
export const DIRECT_MATERIALS = new Set<Material>([
  'composite',
  'amalgam',
  'glass_ionomer',
  'temporary',
]);

export const SURFACE_LETTERS: Record<string, Surface> = {
  M: 'M',
  O: 'O',
  D: 'D',
  I: 'I',
  B: 'B',
  L: 'L',
  P: 'L',
  V: 'B',
};
